import { z } from "zod";
import {
	AdminListInputSchema,
	adminItemOutput,
	adminListOutput,
} from "./common";

// =============
// Admin User Schemas
// =============

/**
 * User role enum.
 */
export const AdminUserRoleSchema = z.enum(["user", "admin", "super_admin"]);
export type AdminUserRole = z.infer<typeof AdminUserRoleSchema>;

/**
 * Admin user schema - includes deletedAt for soft delete visibility.
 */
export const AdminUserSchema = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string().email(),
	emailVerified: z.boolean(),
	image: z.string().nullable(),
	role: AdminUserRoleSchema,
	banned: z.boolean(),
	banReason: z.string().nullable(),
	banExpires: z.coerce.date().nullable(),
	stripeCustomerId: z.string().nullable(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
	deletedAt: z.coerce.date().nullable(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

/**
 * Admin user list input with user-specific filters.
 */
export const AdminUserListInputSchema = AdminListInputSchema.extend({
	filter: z
		.object({
			name: z.string().optional(),
			email: z.string().optional(),
			role: AdminUserRoleSchema.optional(),
			banned: z.coerce.boolean().optional(),
			emailVerified: z.coerce.boolean().optional(),
		})
		.optional(),
});
export type AdminUserListInput = z.infer<typeof AdminUserListInputSchema>;

/**
 * Admin user list output.
 */
export const AdminUserListOutputSchema = adminListOutput(AdminUserSchema);
export type AdminUserListOutput = z.infer<typeof AdminUserListOutputSchema>;

/**
 * Admin user single item output.
 */
export const AdminUserOutputSchema = adminItemOutput(AdminUserSchema);
export type AdminUserOutput = z.infer<typeof AdminUserOutputSchema>;

/**
 * Admin user create input.
 */
export const AdminUserCreateInputSchema = z.object({
	name: z.string().min(1),
	email: z.string().email(),
	role: AdminUserRoleSchema.optional(),
	emailVerified: z.boolean().optional(),
});
export type AdminUserCreateInput = z.infer<typeof AdminUserCreateInputSchema>;

/**
 * Admin user update input.
 */
export const AdminUserUpdateInputSchema = z.object({
	id: z.string(),
	name: z.string().min(1).optional(),
	email: z.string().email().optional(),
	image: z.string().nullable().optional(),
});
export type AdminUserUpdateInput = z.infer<typeof AdminUserUpdateInputSchema>;

/**
 * Admin user ban input.
 */
export const AdminUserBanInputSchema = z.object({
	id: z.string(),
	reason: z.string().optional(),
	expiresAt: z.coerce.date().optional(), // If not provided, permanent ban
});
export type AdminUserBanInput = z.infer<typeof AdminUserBanInputSchema>;

/**
 * Admin user role update input (super_admin only).
 */
export const AdminUserRoleUpdateInputSchema = z.object({
	id: z.string(),
	role: AdminUserRoleSchema,
});
export type AdminUserRoleUpdateInput = z.infer<
	typeof AdminUserRoleUpdateInputSchema
>;
